// CATALOG-DATA-132/137 — client nutrition helpers (null for unknown; never absent→0).

export type NutritionBasis = "per_100g" | "per_100ml" | "per_serving";
export type NutritionSource =
  | "packaging"
  | "supplier"
  | "manual_review"
  | "open_data"
  | "generic_reference"
  | "ai_estimated"
  | "unknown";

export type NutritionQuality =
  | "packaging_verified"
  | "manufacturer_verified"
  | "distributor_verified"
  | "barcode_open_data_verified"
  | "generic_reference"
  | "ai_estimated"
  | "needs_human_review"
  | "unavailable"
  | "not_applicable";

export interface ProductNutritionV2 {
  nutrition_basis: NutritionBasis | null;
  serving_size: string | null;
  energy_kcal: number | null;
  energy_kj: number | null;
  protein_g: number | null;
  fat_g: number | null;
  saturated_fat_g: number | null;
  carbohydrate_g: number | null;
  sugars_g: number | null;
  fiber_g: number | null;
  salt_g: number | null;
  sodium_mg?: number | null;
  nutrition_source: NutritionSource | null;
  nutrition_verified: boolean;
  nutrition_verified_at: string | null;
  nutrition_quality?: NutritionQuality | null;
  nutrition_source_name?: string | null;
  nutrition_source_url?: string | null;
  nutrition_source_retrieved_at?: string | null;
  preparation_state?: string | null;
}

export const EMPTY_NUTRITION_V2: ProductNutritionV2 = {
  nutrition_basis: null,
  serving_size: null,
  energy_kcal: null,
  energy_kj: null,
  protein_g: null,
  fat_g: null,
  saturated_fat_g: null,
  carbohydrate_g: null,
  sugars_g: null,
  fiber_g: null,
  salt_g: null,
  sodium_mg: null,
  nutrition_source: null,
  nutrition_verified: false,
  nutrition_verified_at: null,
  nutrition_quality: null,
  nutrition_source_name: null,
  nutrition_source_url: null,
  nutrition_source_retrieved_at: null,
  preparation_state: null,
};

const NUMERIC_KEYS = [
  "energy_kcal",
  "energy_kj",
  "protein_g",
  "fat_g",
  "saturated_fat_g",
  "carbohydrate_g",
  "sugars_g",
  "fiber_g",
  "salt_g",
] as const;

export function hasDisplayableNutrition(n: ProductNutritionV2 | null | undefined): boolean {
  if (!n) return false;
  if (n.nutrition_quality === "needs_human_review" || n.nutrition_quality === "unavailable") return false;
  return NUMERIC_KEYS.some((k) => n[k] != null) || Boolean(n.serving_size && n.nutrition_basis);
}

export function nutritionBasisLabel(lang: "ua" | "en", basis: NutritionBasis | null): string {
  if (!basis) return lang === "en" ? "Basis unknown" : "Основа невідома";
  if (lang === "en") {
    if (basis === "per_100g") return "per 100 g";
    if (basis === "per_100ml") return "per 100 ml";
    return "per serving";
  }
  if (basis === "per_100g") return "на 100 г";
  if (basis === "per_100ml") return "на 100 мл";
  return "на порцію";
}

export function nutritionSourceBadge(lang: "ua" | "en", quality: NutritionQuality | null | undefined): string {
  const q = quality ?? "unknown";
  if (lang === "en") {
    if (q === "packaging_verified") return "Packaging data";
    if (q === "manufacturer_verified" || q === "distributor_verified") return "Manufacturer data";
    if (q === "barcode_open_data_verified" || q === "generic_reference") return "Open source";
    if (q === "ai_estimated") return "Approximate estimate";
    return "Nutrition data";
  }
  if (q === "packaging_verified") return "Дані з упаковки";
  if (q === "manufacturer_verified" || q === "distributor_verified") return "Дані виробника";
  if (q === "barcode_open_data_verified" || q === "generic_reference") return "Відкрите джерело";
  if (q === "ai_estimated") return "Орієнтовний розрахунок";
  return "Харчові дані";
}

export function nutritionDisclaimer(lang: "ua" | "en", quality: NutritionQuality | null | undefined): string {
  const ai = quality === "ai_estimated";
  if (lang === "en") {
    return ai
      ? "Approximate values calculated from composition and similar products. They may differ substantially from manufacturer information. Not medical or dietetic advice."
      : "Values are shown according to manufacturer, packaging, or an open food database. Actual values may differ by batch and country of origin. Always check the product packaging.";
  }
  return ai
    ? "Орієнтовні дані. Значення розраховані на основі складу та аналогічних продуктів і можуть суттєво відрізнятися від інформації виробника. Не використовуйте їх як медичну або дієтологічну рекомендацію."
    : "Дані наведено відповідно до інформації виробника, упаковки або відкритої бази продуктів. Фактичні значення можуть відрізнятися залежно від партії та країни виробництва. Перевіряйте інформацію на упаковці товару.";
}

export type NutritionRow = { label: string; value: string };

export function buildNutritionDisplayRows(
  lang: "ua" | "en",
  n: ProductNutritionV2,
): NutritionRow[] {
  const rows: NutritionRow[] = [];
  const push = (ua: string, en: string, v: number | null | undefined, unit: string) => {
    if (v == null) return;
    rows.push({ label: lang === "en" ? en : ua, value: `${v} ${unit}` });
  };
  push("Енергія", "Energy", n.energy_kcal, "kcal");
  push("Енергія", "Energy", n.energy_kj, "kJ");
  push("Білки", "Protein", n.protein_g, "g");
  push("Жири", "Fat", n.fat_g, "g");
  push("з них насичені", "of which saturates", n.saturated_fat_g, "g");
  push("Вуглеводи", "Carbohydrate", n.carbohydrate_g, "g");
  push("з них цукри", "of which sugars", n.sugars_g, "g");
  push("Клітковина", "Fibre", n.fiber_g, "g");
  push("Сіль", "Salt", n.salt_g, "g");
  return rows;
}
